import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    UserPlus,
    Calendar,
    ShieldAlert,
    Target,
    Rocket,
    HeartHandshake,
    Wallet,
    Activity,
    ArrowUpRight,
} from "lucide-react";
import { fetchUseCases } from "../lib/cmsApi";
import { hydrateUseCase } from "../lib/cmsIcons";

const FALLBACK_CASES = [
    {
        id: "onboarding",
        icon: UserPlus,
        accent: "text-cyan-300",
        accentBg: "bg-cyan-300/10 border-cyan-300/25",
        ring: "from-cyan-400/30 to-cyan-400/0",
        title: "End-to-end employee onboarding",
        sub: "From signed offer to first sprint, in 14 days",
        challenge:
            "Onboarding lives across HR forms, IT tickets, manager emails and tribal SOPs. New hires lose their first two weeks chasing the company instead of doing the work.",
        solution: [
            "HR triggers the onboarding workflow the moment offer is signed.",
            "Operations auto-provisions accounts, tools and access by role.",
            "Projects assigns a buddy and queues a starter sprint.",
            "Intelligence drafts a personalised first-week plan from past hires.",
            "Communication threads decisions to the hire’s own onboarding doc.",
        ],
        outcome:
            "Time-to-productivity dropped from 6 weeks to 14 days across cohorts.",
    },
    {
        id: "month-end",
        icon: Calendar,
        accent: "text-emerald-300",
        accentBg: "bg-emerald-300/10 border-emerald-300/25",
        ring: "from-emerald-400/30 to-emerald-400/0",
        title: "Month-end close, without the war room",
        sub: "Close the books in 3 days, not 12",
        challenge:
            "Finance pulls invoices from one tool, expenses from another, and timesheets from a third. Reconciling takes a week and the numbers still disagree.",
        solution: [
            "Sales deals carry their invoices; Projects carry their hours.",
            "Finance closes against one ledger that already agrees with itself.",
            "Workflows auto-route accruals and approvals to owners.",
            "Intelligence flags anomalies before the CFO sees them.",
        ],
        outcome:
            "Close time reduced from 12 to 3 business days. Audit prep almost zero.",
    },
    {
        id: "okr-cycle",
        icon: Target,
        accent: "text-amber-glow",
        accentBg: "bg-amber-300/10 border-amber-300/25",
        ring: "from-amber-400/30 to-amber-400/0",
        title: "Running a quarterly OKR cycle",
        sub: "Plan, track and grade goals the company can actually feel",
        challenge:
            "OKRs end up as a deck nobody reads. The work in Projects has no thread back to the company’s objectives, so leadership flies blind.",
        solution: [
            "Set objectives at the company, team and individual level.",
            "Link every project, deal and task to the KR it serves.",
            "Auto-aggregate progress weekly with a narrative summary.",
            "Run grading at quarter-end with evidence already attached.",
        ],
        outcome:
            "OKR confidence scores tripled, and leadership reviews collapsed from 90 to 25 minutes.",
    },
    {
        id: "incident",
        icon: ShieldAlert,
        accent: "text-rose-300",
        accentBg: "bg-rose-300/10 border-rose-300/25",
        ring: "from-rose-400/30 to-rose-400/0",
        title: "Handling a security incident",
        sub: "From first alert to post-mortem, with everything logged",
        challenge:
            "When something breaks, half the time is wasted scrambling for context — who owns what, who was paged, what changed last.",
        solution: [
            "An incident workflow opens with one CLI command or button click.",
            "Ops pages on-call, Communication opens a war-room thread.",
            "Every change, decision and timestamp is captured to the timeline.",
            "Intelligence drafts the post-mortem from the incident graph.",
        ],
        outcome:
            "Mean time to resolution down 38%. Post-mortems shipped same-day, not same-week.",
    },
    {
        id: "fundraise",
        icon: Rocket,
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/25",
        ring: "from-electric/30 to-electric/0",
        title: "Running a fundraise",
        sub: "Data room, pipeline and updates from one workspace",
        challenge:
            "Fundraises are managed in a personal CRM, a Notion data room and a shared inbox. Updates get out of sync and investors notice.",
        solution: [
            "Track every investor as a deal with stage, owner and next step.",
            "Maintain the data room as docs linked to the same investor record.",
            "Auto-generate the monthly investor update from real metrics.",
            "Co-pilot drafts founder follow-ups grounded in the latest numbers.",
        ],
        outcome:
            "Closed rounds 40% faster, with no investor ever asking for the same number twice.",
    },
    {
        id: "client-engagement",
        icon: HeartHandshake,
        accent: "text-violet-300",
        accentBg: "bg-violet-300/10 border-violet-300/25",
        ring: "from-violet-400/30 to-violet-400/0",
        title: "Delivering a client engagement",
        sub: "Quote, project, hours and invoice — on one thread",
        challenge:
            "Quote in one tool, project in another, hours in a third, invoice in a fourth. Margin is impossible to see until the engagement is over.",
        solution: [
            "Quote is generated from Sales and converts into a project on signature.",
            "Projects logs hours, costs and milestones against the engagement.",
            "Finance invoices directly from delivered milestones.",
            "Intelligence reports live margin per engagement.",
        ],
        outcome:
            "Margin visibility per engagement moved from quarterly to real-time.",
    },
    {
        id: "expense",
        icon: Wallet,
        accent: "text-emerald-300",
        accentBg: "bg-emerald-300/10 border-emerald-300/25",
        ring: "from-emerald-400/30 to-emerald-400/0",
        title: "Cleaning up expense chaos",
        sub: "Receipts, approvals and reimbursements on rails",
        challenge:
            "Expense reports live in spreadsheets. Approvals get lost in chat. Reimbursements take weeks and morale takes a hit.",
        solution: [
            "Submit expenses from chat or mobile with the receipt attached.",
            "Workflow routes to manager based on policy and amount.",
            "Approvals are one tap, with audit trail attached.",
            "Reimbursements export to payroll automatically.",
        ],
        outcome:
            "Reimbursement cycle cut from 14 days to under 3.",
    },
    {
        id: "pulse",
        icon: Activity,
        accent: "text-cyan-300",
        accentBg: "bg-cyan-300/10 border-cyan-300/25",
        ring: "from-cyan-400/30 to-cyan-400/0",
        title: "Running a Monday leadership review",
        sub: "Five signals, one screen, twenty-five minutes",
        challenge:
            "Leadership reviews start at 9am and finish whenever the last spreadsheet finally loads. By the time the meeting ends, the decisions are stale.",
        solution: [
            "Auto-aggregated pulse: Revenue, People, Ops, Customer, Cash.",
            "Intelligence drafts the narrative and surfaces the 3 things that changed.",
            "Decisions captured live and pushed back into the affected modules.",
            "Action items live on the records they belong to, not in the deck.",
        ],
        outcome:
            "Leadership reviews shortened to 25 minutes with measurably better decisions.",
    },
];

export default function UseCasesPage() {
    const [cases, setCases] = useState(FALLBACK_CASES);
    const [activeId, setActiveId] = useState(FALLBACK_CASES[0].id);

    useEffect(() => {
        fetchUseCases()
            .then((data) => {
                if (data?.cases?.length) {
                    const hydrated = data.cases.map(hydrateUseCase);
                    setCases(hydrated);
                    setActiveId(hydrated[0].id);
                }
            })
            .catch(() => {});
    }, []);

    const active = cases.find((c) => c.id === activeId) || cases[0];

    return (
        <div className="pt-28 lg:pt-32" data-testid="use-cases-page">
            <section className="relative overflow-hidden py-16 lg:py-20">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // use cases · how teams actually use ApexBrain
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="use-cases-title"
                        >
                            Real workflows.
                            <br />
                            <span className="gradient-text">Real outcomes.</span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        Eight grounded scenarios — from onboarding a new hire
                        to closing the books — and what changes when chaos
                        becomes a system.
                    </p>
                </div>
            </section>

            <section className="relative pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    {/* Tab pills */}
                    <div
                        className="flex flex-wrap gap-2 mb-10"
                        data-testid="use-cases-tabs"
                    >
                        {cases.map((c) => {
                            const isActive = activeId === c.id;
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => setActiveId(c.id)}
                                    data-testid={`use-case-tab-${c.id}`}
                                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
                                        isActive
                                            ? `${c.accentBg} text-white`
                                            : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                                    }`}
                                >
                                    <c.icon
                                        className={`w-3.5 h-3.5 ${isActive ? c.accent : "text-zinc-500"}`}
                                    />
                                    <span>{c.title.split(" — ")[0]}</span>
                                </button>
                            );
                        })}
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.article
                            key={active.id}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } }}
                            exit={{ opacity: 0, y: -6, transition: { duration: 0.18, ease: [0.4, 0, 1, 1] } }}
                            className="relative rounded-2xl border border-white/[0.08] bg-obsidian-200/70 backdrop-blur-sm overflow-hidden"
                            data-testid={`use-case-content-${active.id}`}
                        >
                            <div
                                className={`absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br ${active.ring} blur-3xl pointer-events-none`}
                            />
                            <header className="relative p-7 sm:p-10 border-b border-white/[0.06]">
                                <div className="flex items-center gap-3 mb-4">
                                    <span
                                        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border ${active.accentBg}`}
                                    >
                                        <active.icon
                                            className={`w-4 h-4 ${active.accent}`}
                                        />
                                    </span>
                                    <span
                                        className={`text-[10px] font-code uppercase tracking-[0.2em] font-semibold ${active.accent}`}
                                    >
                                        scenario
                                    </span>
                                </div>
                                <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-white leading-[1.1]">
                                    {active.title}
                                </h2>
                                <p className="mt-3 text-zinc-400 text-base sm:text-lg font-light">
                                    {active.sub}
                                </p>
                            </header>

                            <div className="relative p-7 sm:p-10 space-y-9">
                                <Block label="// the challenge">
                                    <p className="text-zinc-300 leading-relaxed">
                                        {active.challenge}
                                    </p>
                                </Block>

                                <Block label="// how apexbrain handles it">
                                    <ol className="space-y-3">
                                        {active.solution.map((s, i) => (
                                            <li
                                                key={s}
                                                className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
                                            >
                                                <span
                                                    className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border ${active.accentBg} font-code text-[11px] font-semibold ${active.accent}`}
                                                >
                                                    {String(i + 1).padStart(2, "0")}
                                                </span>
                                                <span className="text-sm sm:text-base text-zinc-200">
                                                    {s}
                                                </span>
                                            </li>
                                        ))}
                                    </ol>
                                </Block>

                                <div className="relative rounded-xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-6 overflow-hidden">
                                    <div className="absolute -bottom-20 -left-20 w-56 h-56 bg-electric/15 blur-3xl rounded-full pointer-events-none" />
                                    <p
                                        className={`relative font-code text-[10px] uppercase tracking-[0.2em] mb-2 ${active.accent}`}
                                    >
                                        // outcome
                                    </p>
                                    <p className="relative font-outfit text-lg sm:text-xl text-white leading-snug">
                                        {active.outcome}
                                    </p>
                                </div>

                                <div className="flex flex-wrap items-center gap-3 pt-2">
                                    <a
                                        href="/case-studies"
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white transition"
                                    >
                                        See full case studies
                                        <ArrowUpRight className="w-3 h-3" />
                                    </a>
                                    {/* <a
                                        href="/docs"
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white transition"
                                    >
                                        Open the docs
                                        <ArrowUpRight className="w-3 h-3" />
                                    </a> */}
                                </div>
                            </div>
                        </motion.article>
                    </AnimatePresence>
                </div>
            </section>
        </div>
    );
}

const Block = ({ label, children }) => (
    <div>
        <p className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
            {label}
        </p>
        {children}
    </div>
);
